import React, { useState, useRef, useEffect } from "react";
import "./ChatBot.css";

interface Message {
  role: "user" | "bot";
  text: string;
}

const FAQ: Record<string, string> = {
  "how does it work":
    "Request App lets fans send paid song requests to their favourite artists and performers. Pick a song, set your price, and send the request. The performer sees it in real-time and can accept or decline.",
  "how do i request a song":
    "Find your performer's page (visit their link), search for a song, choose a price, and submit your request. You'll be notified when the performer responds.",
  "how do i become an artist":
    "Click 'Join as Talent' on the home page or go to /register. After signing up and connecting Stripe, you'll get your own request page.",
  "how do i get paid":
    "Earnings accumulate as you accept requests. Tap 'Cashout' on your Requests page to transfer funds to your connected Stripe account. Request App takes a small platform fee.",
  "what is the fee":
    "Request App charges a small platform fee on each transaction. The performer receives the majority of the customer's payment.",
  "can i set my own price":
    "Yes! As a performer, you can set a minimum request price from your Requests page. Customers can offer more than your minimum.",
  "what is priority":
    "Priority (Fast Pass) requests are boosted and appear at the top of the performer's queue. They cost more but increase the chance of being played sooner.",
  "how do i scan the qr code":
    "Open your phone's camera and point it at the performer's QR code. It will open their request page in your browser automatically.",
  "can i decline a request":
    "Yes. Performers can decline any request. If declined, the customer is not charged. You can also provide a reason or suggest a higher amount.",
  "where is my qr code":
    "Go to your Requests page and tap the QR icon in the top-right corner.",
  "how do i change my price":
    "On your Requests page, tap the price display next to your name. Type a new amount and press Enter or tap away to save.",
  "is request app free":
    "Creating an account is free. The platform fee only applies when a paid request is successfully completed.",
  cashout:
    "Tap 'Cashout' on the Requests page. You'll see a breakdown of earnings and fees. Confirm to transfer to your Stripe account.",
  stripe:
    "Request App uses Stripe for payments. During registration, you'll connect your Stripe account. Payouts go directly to your Stripe balance.",
  refund:
    "If a performer declines your request, you are not charged. For disputes on accepted requests, contact support.",
  support:
    "For support, email us at support@requesta.app.",
};

function findAnswer(input: string): string {
  const lower = input.toLowerCase().trim();
  for (const [key, answer] of Object.entries(FAQ)) {
    if (lower.includes(key)) return answer;
  }
  if (lower.includes("hello") || lower.includes("hi") || lower.includes("hey")) {
    return "Hey there! I'm the Request App assistant. Ask me anything about how the platform works, pricing, payouts, or how to get started as a performer.";
  }
  if (lower.includes("thank")) {
    return "You're welcome! Let me know if you have any other questions.";
  }
  return "I'm not sure about that. Try asking about how to request songs, payouts, pricing, or getting started. For complex issues, reach out to our support team at support@requesta.app.";
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "bot",
      text: "Hi! I'm the Request App assistant. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-chatbot", handler);
    return () => window.removeEventListener("open-chatbot", handler);
  }, []);

  const send = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const userMsg: Message = { role: "user", text: trimmed };
    const botMsg: Message = { role: "bot", text: findAnswer(trimmed) };
    setMessages((prev) => [...prev, userMsg, botMsg]);
    setInput("");
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        className="chatbot-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chat"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2L9.5 8.5 3 12l6.5 3.5L12 22l2.5-6.5L21 12l-6.5-3.5Z" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
          </svg>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-ai-badge">AI</div>
              <span>Request App Assistant</span>
            </div>
            <button className="chatbot-close" onClick={() => setOpen(false)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg-${m.role}`}>
                {m.text}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="chatbot-input-row">
            <input
              className="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask a question..."
              autoFocus
            />
            <button className="chatbot-send" onClick={send} disabled={!input.trim()}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
